import React, { useState } from 'react';
import MetricCard, { assess } from '../twin/MetricCard';
import JourneyTrack from '../twin/JourneyTrack';
import DialTargetEditor from './DialTargetEditor';

// A full-width "Dial": one metric. Collapsed, it's a glance — title, a colored
// progress-to-target ring, a trend arrow, and the trajectory chart. The full
// readouts + this week's grade live behind a "Details" toggle. AI coaching is
// surfaced up-front only where you're scoring poorly; otherwise it's in Details.

// Render coaching copy with the numbers picked out bright + bold.
const emphasize = (text) =>
  String(text)
    .split(/(\d[\d,.:%]*)/g)
    .map((part, i) =>
      /^\d/.test(part)
        ? <strong key={i} className="font-semibold text-gray-900 dark:text-white">{part}</strong>
        : part
    );

// Progress to target: 0–100% of the way from where you started to your target
// band, direction-aware (distance-to-goal, so up/down/range all work). 100% once
// you've reached the band, 0% if you've drifted back past your start.
const progressToTarget = (card) => {
  const { start_value: start, now, ultimate_low: lo, ultimate_high: hi, direction } = card;
  if (start == null || now == null) return null;
  const distToGoal = (v) => {
    if (direction === 'up') return lo == null ? null : Math.max(0, lo - v);
    if (direction === 'down') return hi == null ? null : Math.max(0, v - hi);
    if (lo == null || hi == null) return null;
    return v < lo ? lo - v : v > hi ? v - hi : 0;
  };
  const startDist = distToGoal(start);
  const nowDist = distToGoal(now);
  if (startDist == null || nowDist == null) return null;
  if (nowDist === 0) return 100;
  if (startDist === 0) return 0;
  return Math.max(0, Math.min(100, Math.round(((startDist - nowDist) / startDist) * 100)));
};

// Traffic-light fill: green when you're most of the way there, amber partway,
// red when you've barely started. (This is a data meter, distinct from the
// gold "advice" accent used for AI copy.)
const ringColor = (pct) =>
  pct >= 67
    ? 'text-good-strong dark:text-good'
    : pct >= 34
      ? 'text-amber-500 dark:text-amber-400'
      : 'text-off-strong dark:text-off';

const ProgressRing = ({ pct }) => {
  const size = 54;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div
      className={`relative inline-flex shrink-0 items-center justify-center ${ringColor(pct)}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="opacity-20" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <span className="absolute text-sm font-bold tabular-nums text-gray-900 dark:text-white">
        {pct}
      </span>
    </div>
  );
};

// Are you trending the RIGHT WAY toward your goal? Arrow + word + colour all say
// the same thing, so it reads the same whether the metric is higher- or
// lower-is-better (no mental math): up/green "Improving", down/red "Wrong way",
// sideways/gray "Holding steady".
const TREND = {
  good: { rot: 'rotate-0', cls: 'text-good-strong dark:text-good', label: 'Improving' },
  bad: { rot: 'rotate-180', cls: 'text-off-strong dark:text-off', label: 'Wrong way' },
  flat: { rot: 'rotate-90', cls: 'text-gray-400 dark:text-gray-500', label: 'Holding steady' },
};
const TrendArrow = ({ tone }) => {
  const t = TREND[tone] || TREND.flat;
  return (
    <span className={`inline-flex items-center gap-1.5 ${t.cls}`}>
      <svg
        className={`w-5 h-5 ${t.rot}`}
        fill="none" stroke="currentColor" strokeWidth={2.25} viewBox="0 0 24 24" aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5M5 12l7-7 7 7" />
      </svg>
      <span className="text-sm font-semibold">{t.label}</span>
    </span>
  );
};

const DialCard = ({ card, metric, target, onTargetSaved, onUnpin }) => {
  const hasChart = metric && metric.enough_data;
  const noData = card.now == null;
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState(false);
  const [tuning, setTuning] = useState(false);

  const tone = hasChart ? assess(metric).tone : null; // good | bad | flat (right-way?)
  const progress = progressToTarget(card);
  const poorly = progress != null && progress < 50; // surface advice up-front here
  const showTip = !hasChart || poorly || open;

  return (
    <div className="card-glow rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5 transition-shadow hover:shadow-card">
      <div className="mb-3">
        {confirming ? (
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white min-w-0 break-words">
              Remove {card.label}?
            </h3>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => onUnpin(card.metric_key)}
                aria-label={`Confirm removing ${card.label}`}
                className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-off/15 text-off-strong dark:text-off hover:bg-off/25 border-none transition-colors"
              >
                Remove
              </button>
              <button
                onClick={() => setConfirming(false)}
                aria-label="Keep this dial"
                className="text-sm font-medium px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 bg-transparent border-none transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white min-w-0 break-words flex-1">
                {card.label}
              </h3>
              <button
                onClick={() => setConfirming(true)}
                aria-label={`Remove ${card.label} from home`}
                title="Remove from home"
                className="shrink-0 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-transparent border-none transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Data-viz status: colored progress ring + trend arrow (no wordy chips) */}
            {(progress != null || tone) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                {progress != null && <ProgressRing pct={progress} />}
                {hasChart && <TrendArrow tone={tone} />}
              </div>
            )}
          </>
        )}
      </div>

      {/* Chart (always visible) — or the compact start→now→target track when
          there's not enough history to draw the trajectory yet. */}
      {hasChart ? (
        <MetricCard metric={metric} part="chart" bare />
      ) : (
        <>
          <JourneyTrack
            start={card.start_value}
            now={card.now}
            low={card.ultimate_low}
            high={card.ultimate_high}
            next={card.next_value}
            nextWeeks={card.next_horizon_weeks}
            direction={card.direction}
            unit={card.unit}
          />
          {!noData && (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              Gathering data — the chart appears once there's enough history.
            </p>
          )}
        </>
      )}

      {/* Details — full readouts + this week's grade, tucked away. */}
      {hasChart && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-transparent border-none transition-colors"
          >
            {open ? 'Hide details' : 'Details'}
            <svg
              className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open && (
            <div className="mt-3">
              <MetricCard metric={metric} part="details" bare />
            </div>
          )}
        </>
      )}

      {/* Adjust target — the dial itself. Collapsed by default so the card stays
          a glance; open it to turn the knob (reference range, custom, opt-out). */}
      {target && onTargetSaved && (
        <>
          <button
            type="button"
            onClick={() => setTuning((v) => !v)}
            aria-expanded={tuning}
            className="mt-3 ml-5 inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-transparent border-none transition-colors"
          >
            {tuning ? 'Done adjusting' : 'Adjust target'}
            <svg
              className={`w-4 h-4 transition-transform ${tuning ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {tuning && (
            <div className="mt-3">
              <DialTargetEditor target={target} onSaved={onTargetSaved} />
            </div>
          )}
        </>
      )}

      {/* AI "how to move this" tip — up-front when scoring poorly, otherwise
          revealed with Details. */}
      {showTip &&
        (card.suggestion ? (
          <p className="mt-3 text-base leading-relaxed text-stone-700 dark:text-stone-200">
            {emphasize(card.suggestion)}
          </p>
        ) : card.suggestion_stale ? (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            Personalizing your next step…
          </p>
        ) : null)}
    </div>
  );
};

export default DialCard;
